import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
/*
 * Participant display labels stamped onto a group thread at creation.
 *
 * WHY (Codex review of #796): the 100-plus-you cap exists so mixed groups
 * like the 37-person Jr Staff group (staff plus guardian stand-ins) work as
 * regular threads. But a family participant can read only their own users
 * doc, so without stamped labels every other member of that room renders as
 * an eight-character uid and drops out of the @mention picker. Whole-family
 * threads solved the same problem with server-stamped participantLabels
 * (familyMessage.mjs); this is that pattern for the staff-created group
 * lane, and Thread.tsx / ThreadRow.tsx already prefer the stamp over a
 * directory miss (threadUserLabel).
 *
 * TRUST: the STAFF CREATOR stamps names read from the same directory the
 * recipient picker just showed them - staff directory names plus the honest
 * guardian stand-in labels ("Parent of Ava Bell"). That is the trust level
 * the family-message lane already accepts for its server stamp: names an
 * active staff caller could already read, written once at create and
 * immutable after (the threads update lane blocks participantLabels). This
 * is NOT the channel notify.mjs's externalSenderPresentation refuses to
 * trust - a self-editable profile name read at DELIVERY time - because the
 * stamp is fixed at create by the staff account starting the conversation,
 * and no participant can rewrite it afterwards.
 *
 * NAMES ARE NEVER INVENTED: a uid with no directory or stand-in label is
 * left unstamped, and readers keep the honest short-uid fallback rather
 * than a guessed name.
 *
 * Also holds the shared @mention-handle assignment (compactLabelToHandle,
 * assignMentionHandles, extractMentionedUids) added for Codex round 5 of
 * #796: every stamped label read here needs a UNIQUE mention handle, not
 * just an honest display string, so the two collapse cleanly into one
 * module.
 *
 * Importless so the unit tests can pin the behavior directly.
 */

/** Mirrors the 120-char bound familyMessage.mjs's serverLabel applies. */
export const MAX_PARTICIPANT_LABEL_LENGTH = 120;

/**
 * Generous bound for a stored mention handle: a compacted label
 * (compactLabelToHandle) can run as long as the label itself, plus a
 * collision-suffix digit or two. Anything past this is not a shape this
 * module's own assignment logic could ever produce.
 */
export const MAX_PARTICIPANT_HANDLE_LENGTH = 160;

/**
 * Keep only labels for uids actually on the thread, trimmed, non-empty,
 * and bounded. Everything else is dropped rather than repaired: an entry
 * for a non-participant is a forgery shape the rules also refuse, and an
 * empty name is a gap to show honestly, not to fill.
 */
export function cleanParticipantLabels(
  participantUids: readonly string[],
  raw: Readonly<Record<string, string>> | undefined
): Record<string, string> {
  const labels: Record<string, string> = {};
  if (!raw) return labels;
  for (const uid of participantUids) {
    const value = raw[uid];
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) continue;
    labels[uid] = trimmed.slice(0, MAX_PARTICIPANT_LABEL_LENGTH);
  }
  return labels;
}

/**
 * The ONE way to READ a stamped label (Codex review of #796, round 3).
 *
 * The rules fence participantLabels to a map whose keys sit on the roster,
 * but Firestore rules cannot iterate the VALUES of a map with arbitrary
 * keys, so per-value string validation is not available on the write side:
 * a stale or custom staff client can legally store a number, timestamp, or
 * nested map as a "name", and the stamp is immutable after create, so a
 * poisoned thread could never be repaired. Readers therefore harden here
 * instead: anything that is not a non-empty string WITHIN THE 120-CHAR
 * BOUND comes back null, and every caller falls back to its pre-stamp
 * directory or short-uid path - the room degrades to exactly the old
 * behavior instead of throwing on .trim() and taking the whole
 * conversation down with it. The length check is rejection, not
 * truncation, on purpose (Codex review of #796, round 4): every
 * conforming writer slices to the bound before stamping, so an oversized
 * value proves a non-conforming client, and this module's posture for
 * those is the honest fallback, never a repaired prefix - which also
 * keeps a near-document-sized "name" out of every render surface and out
 * of userHandle/mention-regex construction.
 */
export function stampedParticipantLabel(
  labels: Readonly<Record<string, unknown>> | undefined,
  uid: string
): string | null {
  const raw = labels?.[uid];
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed || trimmed.length > MAX_PARTICIPANT_LABEL_LENGTH) return null;
  return trimmed;
}

/**
 * Compacts a stamped participant label ("Parent of Ava Bell") to a plain-
 * alphanumeric, CamelCase mention-handle token ("ParentOfAvaBell").
 *
 * WHY (Codex round 5 of #796): a stamped-only member has no directory
 * userHandle to fall back on, and a stamped label is exactly the string a
 * mention handle is NOT allowed to be - it has spaces, it can carry
 * punctuation, and extractMentionedUids anchors every handle with \b, so a
 * handle must be pure word characters and must never END on a non-word
 * character (a trailing punctuation mark leaves \b with nothing to
 * transition against, and the mention silently never matches at the end of
 * a message). Splitting on every run of non-alphanumeric characters and
 * capitalizing what survives keeps the token both a valid handle and
 * legible enough that "@ParentOfAvaBell" still reads as the label it came
 * from, not an opaque hash.
 *
 * Returns '' when nothing alphanumeric survives (a fully non-ASCII or
 * fully-punctuation label) - the caller falls back to the short-uid handle,
 * the same fallback an unnamed participant already gets from userHandle.
 */
export function compactLabelToHandle(label: string): string {
  return label
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

/**
 * Base mention-handle candidate for a STAMPED-ONLY participant (no
 * directory doc, or one an immutable stamp names anyway). A participant
 * this thread never stamped keeps userHandle's own
 * first-token-plus-short-uid-fallback behavior below, unchanged on purpose
 * (do not change how a lone "Claire" reads in an unstamped thread).
 */
export function stampedMentionBaseHandle(uid: string, label: string): string {
  const compacted = compactLabelToHandle(label);
  return compacted || uid.slice(0, 6);
}

/**
 * Assigns every thread participant a UNIQUE @mention handle from a
 * base-handle candidate, in participantUids order (stable, from the thread
 * doc) - so the same thread doc assigns the same handles on every render,
 * for every viewer.
 *
 * THE FIX (Codex round 5 of #796, CONFIRMED by the PM): userHandle keeps
 * only the FIRST token of a display name, so every "Parent of X" stand-in
 * label collapsed to the identical base handle "Parent" - mentioning ONE
 * guardian therefore extracted and notified EVERY guardian sharing that
 * base (11 stand-ins in the Jr Staff group, for one @mention). The first
 * participant to reach a base handle keeps it bare; each later participant
 * landing on the same base gets the smallest integer suffix (2, 3, ...)
 * not already taken by ANY assigned handle - the exact same rule covers
 * the stand-in collision, two guardians of the SAME dancer (identical
 * labels), and the pre-existing two-directory-Claires case alike.
 *
 * A `baseHandle` of null means nobody can name this participant yet (no
 * directory doc and no trusted stamp) - excluded from the map entirely,
 * the same as before this fix. Nothing here invents a name.
 *
 * Both the insert side (the @mention picker) and the extract side (the
 * send/edit-time regex scan) must build their handle from THIS ONE map,
 * never recompute one separately, or the two can drift out of agreement -
 * which is exactly the bug this fix closes.
 */
export function assignMentionHandles(
  participants: readonly { uid: string; baseHandle: string | null }[]
): Map<string, string> {
  const handleByUid = new Map<string, string>();
  const taken = new Set<string>();
  for (const { uid, baseHandle } of participants) {
    if (!baseHandle) continue;
    let handle = baseHandle;
    let suffix = 2;
    while (taken.has(handle)) {
      handle = `${baseHandle}${suffix}`;
      suffix += 1;
    }
    taken.add(handle);
    handleByUid.set(uid, handle);
  }
  return handleByUid;
}

/**
 * Escapes a handle for safe embedding inside extractMentionedUids' regex.
 * Every handle THIS module assigns is already [A-Za-z0-9]+ (compaction
 * strips everything else, and the short-uid and integer-suffix fallbacks
 * are alphanumeric too), so nothing here needs escaping for a handle this
 * module produced - this exists only because a directory handle (userHandle's
 * first-name token, lib/threads.ts) predates this fix and is not itself
 * constrained to word characters, so a name whose first token carries a
 * regex metacharacter must not have it read as one.
 */
function escapeForMentionRegex(handle: string): string {
  return handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Scans `text` for every `@<handle>` substring that names a thread
 * participant and returns their uids. THE ONE extractor: Thread.tsx's
 * composer send/edit path and ThreadRow.tsx's inline-reply edit path both
 * call this against the SAME handleByUid the picker built, so insert and
 * extract read identically and can never disagree (Codex round 5 of #796).
 */
export function extractMentionedUids(
  text: string,
  handleByUid: ReadonlyMap<string, string>
): string[] {
  text = plainMessageText(text);
  const uids: string[] = [];
  for (const [uid, handle] of handleByUid.entries()) {
    const pattern = new RegExp(`(^|\\s)@${escapeForMentionRegex(handle)}\\b`);
    if (pattern.test(text)) uids.push(uid);
  }
  return uids;
}

/**
 * Pull a stable "handle" from a user doc for @-mention syntax: the first
 * whitespace-separated token of displayName, so a typed `@Bobbie` resolves
 * to "Bobbie Bford" cleanly.
 *
 * NOT the email local part. "jane.doe" rendered as a handle in Thread.tsx
 * and ThreadRow.tsx is still contact information, and it is most of an
 * address once you know the studio's domain.
 *
 * Lives here rather than in lib/threads.ts (which re-exports it for its
 * existing importers) so the whole mention-handle system sits in one
 * importless module the unit tests can execute directly - lib/threads.ts
 * pulls the Firebase client graph and cannot be imported by a node test,
 * which is why round 5's assertions could only read its source.
 */
export function userHandle(u: {
  displayName?: string | null;
  email?: string | null;
  uid: string;
}): string {
  const dn = u.displayName?.trim();
  if (dn) {
    const first = dn.split(/\s+/)[0];
    if (first) return first;
  }
  return u.uid.slice(0, 6);
}

/**
 * The ONE way to READ stored participantHandles (Codex round 8 of #796,
 * mirroring stampedParticipantLabel above). A server-created group thread
 * (netlify/functions/lib/threadGroupCreate.mjs) persists this map once, at
 * create, assigned through the exact same assignMentionHandles logic the
 * server imports from this file - so by construction every value is
 * already unique, pure word characters, and stable for the life of the
 * thread: a member leaving later can never renumber anyone else's handle
 * (finding 2). Readers still hardens rather than trusts: anything not a
 * non-empty, bounded, pure-alphanumeric string is dropped, and a repeated
 * value (a poisoned or hand-edited legacy document) keeps only the FIRST
 * uid that claims it, so a collision degrades to one honest gap rather
 * than two people silently sharing a handle.
 */
export function stampedParticipantHandles(
  handles: Readonly<Record<string, unknown>> | undefined,
  participantUids: readonly string[]
): Map<string, string> {
  const out = new Map<string, string>();
  if (!handles) return out;
  const taken = new Set<string>();
  for (const uid of participantUids) {
    const raw = handles[uid];
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    if (!trimmed || trimmed.length > MAX_PARTICIPANT_HANDLE_LENGTH) continue;
    if (!/^[A-Za-z0-9]+$/.test(trimmed)) continue;
    if (taken.has(trimmed)) continue;
    taken.add(trimmed);
    out.set(uid, trimmed);
  }
  return out;
}

/**
 * ONE shared map of participant uid -> unique @mention handle for a
 * thread, consumed by both the @mention picker AND the send/edit-time
 * extraction regex in Thread.tsx and ThreadRow.tsx (Codex round 5 of
 * #796), so insert and extract can never disagree.
 *
 * STORED HANDLES WIN OVER EVERYTHING (Codex round 8 of #796). A
 * server-created group thread persists participantHandles once, at
 * create, immutable after (shared/firestore.rules' update lane blocks
 * changing it) - assigned by roster POSITION is exactly the bug finding 2
 * closed: recomputing on every render meant an early member leaving
 * renumbered every later collision suffix, silently breaking historical
 * mentions and dropping their uid from an edit's re-extraction. Preferring
 * the stored map whenever the thread carries one makes every handle stable
 * BY CONSTRUCTION, for the life of the thread, no matter who leaves.
 *
 * THE STAMP WINS OVER THE DIRECTORY for a thread with no stored handles at
 * all (Codex round 6 of #796) - the derivation below, unchanged from round
 * 6/7. The directory map holds DIFFERENT contents for different viewers of
 * the same room: a staff viewer resolves the whole directory, while a
 * family participant receives only their own user doc, which is the
 * entire reason participantLabels exists. Deriving a handle from the
 * directory first therefore made the handle VIEWER-dependent - staff saw
 * "@Claire" where a guardian saw "@MsClaire" for one person - so a mention
 * one side inserted was not matched by the other side's extractor, and
 * retyping the handle you could see notified nobody. participantLabels is
 * stamped once at create and immutable after, so it is the only name in
 * the room every viewer reads identically: prefer it whenever present, and
 * fall back to the directory only for a participant it does not name (an
 * older thread carrying no stamp at all, where every viewer who can name
 * anyone falls back together).
 *
 * A uid with neither a stored handle, a trusted stamp, nor a directory doc
 * is left out of the map entirely - nobody can mention or be notified as
 * someone this room cannot name.
 */
export function threadMentionHandles(
  thread: {
    participantUids: readonly string[];
    participantLabels?: Readonly<Record<string, unknown>>;
    participantHandles?: Readonly<Record<string, unknown>>;
  },
  usersByUid: ReadonlyMap<string, { displayName?: string | null; email?: string | null; uid: string }>
): Map<string, string> {
  const stored = stampedParticipantHandles(thread.participantHandles, thread.participantUids);
  if (stored.size > 0) return stored;
  return assignMentionHandles(thread.participantUids.map((uid) => {
    const label = stampedParticipantLabel(thread.participantLabels, uid);
    if (label) return { uid, baseHandle: stampedMentionBaseHandle(uid, label) };
    const u = usersByUid.get(uid);
    return { uid, baseHandle: u ? userHandle(u) : null };
  }));
}

/**
 * The pre-round-6 base-handle candidate for one participant: directory
 * first (userHandle's first-name-token), the stamp only as a fallback for
 * a participant with no directory doc. This is EXACTLY what
 * threadMentionHandles computed before Codex round 6 flipped the
 * precedence - reintroduced here, deliberately, as history rather than as
 * today's rule.
 *
 * THE STAMP FALLBACK ITSELF (Codex round 12 of #796, finding A). The
 * pre-round-5 implementation this reconstructs (Thread.tsx, before commit
 * 8e6085c0, around what was then line 1091) read:
 *
 *   const u = usersByUid.get(uid);
 *   const displayName = stampedParticipantLabel(thread.participantLabels, uid);
 *   if (u) m.set(uid, userHandle(u));
 *   else if (displayName) m.set(uid, userHandle({ uid, displayName }));
 *
 * - it passed the stamped label straight through userHandle, taking only
 * its FIRST TOKEN ("Mia" from "Mia Ford"). stampedMentionBaseHandle (the
 * full CamelCase compaction, "MiaFord") is a ROUND 5 invention, built to
 * stop every "Parent of X" stand-in from colliding on the bare handle
 * "Parent" - it did not exist yet at the point in history this function
 * reconstructs, so calling it here as the "old" behavior was wrong: a
 * family viewer with NO directory entry for the other participant (the
 * exact shape usersByUid has for a family account, since rules block
 * reading anyone else's users/{uid} doc) never fell into this branch in a
 * test that also gave both participants directory docs, so the mistake
 * went unnoticed - re-verified false by reading the actual pre-round-5
 * source rather than trusting this function's own comment. Fixed to call
 * userHandle on the label directly, exactly reproducing the historical
 * first-token derivation, so a historical "@Mia" chip renders and survives
 * an edit for every viewer, directory or not.
 */
function legacyMentionBaseHandle(
  uid: string,
  thread: { participantLabels?: Readonly<Record<string, unknown>> },
  usersByUid: ReadonlyMap<string, { displayName?: string | null; email?: string | null; uid: string }>
): string | null {
  const u = usersByUid.get(uid);
  if (u) return userHandle(u);
  const label = stampedParticipantLabel(thread.participantLabels, uid);
  return label ? userHandle({ uid, displayName: label }) : null;
}

/**
 * Legacy mention-handle ALIASES (Codex round 8 of #796, finding 3): for a
 * thread with no stored participantHandles, a participant whose CURRENT
 * handle (threadMentionHandles above) differs from what the pre-round-6
 * algorithm would have produced for them keeps that old form as an extra,
 * read-only alias. Without this, a dancer-staff thread stamped before
 * round 6 - which stores a dancer's full name ("Mia Ford") as her label -
 * silently stopped rendering "@Mia" the moment round 6 shipped (the
 * current handle became "@MiaFord"), and an unrelated edit to that message
 * would re-extract mentions and drop her uid from the Mentions inbox for
 * good.
 *
 * SERVER-STAMPED THREADS NEVER GET ALIASES: their handles are immutable
 * and were never assigned under the old algorithm, so there is no
 * historical form to reach for - guessing one would risk manufacturing a
 * collision that was never real.
 *
 * AMBIGUITY IS DISQUALIFYING, on purpose ("stays dead rather than
 * guessing"): an alias is kept only when its string value names EXACTLY
 * ONE participant across every current handle and every other alias
 * candidate in the room. Two people who would both legacy-alias to "Mia"
 * (or one whose alias collides with someone else's real CURRENT handle)
 * are each too ambiguous to resurrect safely, so both are dropped rather
 * than either guessing which one old text meant.
 */
export function threadLegacyMentionAliases(
  thread: {
    participantUids: readonly string[];
    participantLabels?: Readonly<Record<string, unknown>>;
    participantHandles?: Readonly<Record<string, unknown>>;
  },
  usersByUid: ReadonlyMap<string, { displayName?: string | null; email?: string | null; uid: string }>
): Map<string, string> {
  const stored = stampedParticipantHandles(thread.participantHandles, thread.participantUids);
  if (stored.size > 0) return new Map();

  const current = threadMentionHandles(thread, usersByUid);
  const candidates = new Map<string, string>();
  for (const uid of thread.participantUids) {
    const base = legacyMentionBaseHandle(uid, thread, usersByUid);
    if (!base || base === current.get(uid)) continue;
    candidates.set(uid, base);
  }
  if (candidates.size === 0) return candidates;

  const countOf = new Map<string, number>();
  const bump = (value: string) => countOf.set(value, (countOf.get(value) ?? 0) + 1);
  for (const handle of current.values()) bump(handle);
  for (const handle of candidates.values()) bump(handle);

  const aliases = new Map<string, string>();
  for (const [uid, handle] of candidates) {
    if (countOf.get(handle) === 1) aliases.set(uid, handle);
  }
  return aliases;
}

/**
 * Re-extract @mentions from an EDITED message body, retaining any uid the
 * message already had recorded, even one whose only textual form
 * (`legacyMentionBaseHandle`) is now too AMBIGUOUS to attribute to fresh
 * text (Codex round 14 of #796).
 *
 * THE BUG THIS FIXES. Two participants whose OLD (pre-round-6) handles
 * collided - two Claires - each stamp "@Claire" into a message's stored
 * `mentions` array once, back when the old extractor could not tell them
 * apart either and just matched both. threadLegacyMentionAliases' own
 * ambiguity rule ("stays dead rather than guessing") then, correctly,
 * refuses to hand out "Claire" as a legacy alias for EITHER of them going
 * forward - a NEW mention typed as "@Claire" today has no honest way to
 * know which Claire is meant. But editing ANY part of that OLD message
 * used to re-run the plain extractor with no memory of what the message
 * already meant, so it kept only whichever Claire's CURRENT handle still
 * happened to read "@Claire" and silently dropped the other from the
 * Mentions inbox - an edit to an unrelated word erased a real mention.
 *
 * THE FIX: the message's OWN recorded `existingMentionUids` is the
 * disambiguation. The old extractor already resolved this exact text to
 * these exact uids once; ambiguity blocks a NEW attribution, never the
 * retention of one already on record. A uid already in
 * `existingMentionUids` stays mentioned as long as EITHER its current
 * handle OR its raw legacy candidate (computed the same way
 * threadLegacyMentionAliases does internally, ambiguity check skipped on
 * purpose - the existing record IS the disambiguation) still appears as a
 * literal `@handle` token in the edited text. Deleting the "@Claire" text
 * itself still correctly drops both Claires: retention requires the text
 * to remain, never invents a mention nothing in the body names any more.
 *
 * A NEW ambiguous handle typed into the edit (nobody's uid was already in
 * `existingMentionUids` for it) still attributes to nobody - this
 * function only ever WIDENS the result for a uid the caller already
 * vouches for, never for one it does not.
 *
 * Passing an empty (or omitted) `existingMentionUids` is EXACTLY today's
 * fresh-send behavior: current handles, then legacy aliases, unioned -
 * byte-identical to calling `extractMentionedUids` twice by hand, because
 * that is literally what happens when the retention loop below has
 * nothing to iterate.
 *
 * A DEPARTED MEMBER IS NEVER RETAINED (Codex round 15, finding B). This
 * loop can only ever ADD a uid, so without a check of its own it could add
 * back a uid whose member left the group between the original send and
 * this edit - `netlify/functions/messageEdit.mjs` then rejects the
 * WHOLE edit as `bad_mentions` because that uid is no longer in
 * `participantUids`, so a legitimate edit (fixing a typo, say) would fail
 * outright over a mention nobody asked to keep. Retention now additionally
 * requires the uid to still be a LIVE participant of `thread` (the
 * function already receives it - no new read, no new parameter). A
 * departed member's mention is simply dropped on edit, which is the exact
 * outcome the server would force anyway; the edit itself still saves.
 */
export function extractMentionedUidsForEdit(
  text: string,
  handleByUid: ReadonlyMap<string, string>,
  legacyAliasByUid: ReadonlyMap<string, string>,
  thread: {
    participantUids: readonly string[];
    participantLabels?: Readonly<Record<string, unknown>>;
  } | null | undefined,
  usersByUid: ReadonlyMap<string, { displayName?: string | null; email?: string | null; uid: string }>,
  existingMentionUids: readonly string[] = []
): string[] {
  const current = extractMentionedUids(text, handleByUid);
  const legacy = legacyAliasByUid.size === 0 ? [] : extractMentionedUids(text, legacyAliasByUid);
  const result = new Set([...current, ...legacy]);
  if (!thread) return Array.from(result);
  const liveParticipantUids = new Set(thread.participantUids);
  for (const uid of existingMentionUids) {
    if (result.has(uid)) continue;
    if (!liveParticipantUids.has(uid)) continue;
    const candidateHandle = legacyMentionBaseHandle(uid, thread, usersByUid);
    if (!candidateHandle) continue;
    if (extractMentionedUids(text, new Map([[uid, candidateHandle]])).length > 0) {
      result.add(uid);
    }
  }
  return Array.from(result);
}
